// Module: closeOwner.js
// Purpose: The ONE user-facing label mapping for `eod_close_owner` (#118 D41).
//
//! #118 WP9 (D41) — DeeTrade has exactly TWO close owners and exactly TWO ways to say so, and
//!  they must read identically at every surface. The stored enum values are UNCHANGED
//!  (`broker_rms`, `platform_first`); only the label is mapped.
//!
//!  The old copy is FORBIDDEN and enforced by a repo guard
//!  (`test_118_delete_guards.py::test_r6_8_obsolete_frontend_copy_deleted`). It is deliberately
//!  NOT quoted anywhere in this file — the guard greps for it, so quoting it here would make the
//!  module that replaces it the last surviving occurrence.
//!  It was wrong twice over: the broker-side option was jargon that said nothing about who
//!  actually closes your position, and the platform-side option named a mechanism that was
//!  dark-shipped and never ran — the setting read as a guarantee the product did not give
//!  (see the 15:20-sweep-after-a-15:00-broker-square-off incident on 2026-08-26).
//!
//!  The replacement says exactly WHO closes: the actual broker, resolved from the deployment's
//!  own account(s), or DeeTrade.
//!
//! This module exists so the mapping is defined ONCE. It is consumed by both close-owner
//!  surfaces — `StrategyBuilder/components/DeploymentSection.jsx` (deploy-time) and
//!  `AlgoTrading/components/LiveDeploymentSettingsModal.jsx` (live-edit-time). Two copies of a
//!  money-path label is exactly the drift #118 spent its decommission budget deleting.

// --> Fallback when no account/broker has resolved yet. Deliberately still names an owner
//     ("your broker") rather than falling back to jargon or an empty parenthetical.
const UNRESOLVED_BROKER = 'your broker';

/**
 * eodCloseOwnerLabel — the user-facing label for a stored `eod_close_owner` value.
 *
 * @param {string} owner        stored enum value: 'broker_rms' | 'platform_first'
 * @param {string[]} brokerNames deduped broker names for the deployment's account(s)
 * @returns {string} "Broker square-off (Flattrade)" | "DeeTrade square-off"
 */
export const eodCloseOwnerLabel = (owner, brokerNames) => {
  if (owner === 'broker_rms') {
    const list = brokerNames && brokerNames.length ? brokerNames.join(', ') : UNRESOLVED_BROKER;
    return `Broker square-off (${list})`;
  }
  return 'DeeTrade square-off';
};

/**
 * brokerNamesFromRows — deduped broker names from any per-account row list carrying `broker_id`.
 * Used by the live-settings modal, whose `allocRows` already carry `broker_id` per account, so
 * this needs no extra fetch.
 *
 * @param {Array<{broker_id?: string}>} rows
 * @returns {string[]}
 */
export const brokerNamesFromRows = (rows) => {
  const brokers = new Set();
  (rows || []).forEach((r) => {
    if (r?.broker_id) brokers.add(String(r.broker_id));
  });
  return Array.from(brokers);
};
